import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { theme } from '../styles/theme';
import './ChatRoomListItem.css';

/**
 * ChatRoomListItem - Displays a chat room in the list
 * Matches Flutter ChatRoomListItem design
 */
const ChatRoomListItem = ({ room, currentUserId, currentUserRole, onTap, isOnline }) => {
  // Find other participant with proper type handling
  // For admin users, currentUserId should already be formatted as admin_{agencyId}
  let otherParticipant = null;
  
  // Try using helper method first
  if (room.getOtherParticipant && typeof room.getOtherParticipant === 'function') {
    otherParticipant = room.getOtherParticipant(currentUserId);
    // Validate that we got a valid participant with a name
    if (otherParticipant && (!otherParticipant.userName || otherParticipant.userName === 'undefined')) {
      otherParticipant = null; // Try again
    }
  }
  
  // Fallback: direct participant search
  if (!otherParticipant && room.participants && Array.isArray(room.participants)) {
    const searchUserId = String(currentUserId || '');
    
    // First, try to find participant that's not current user with valid userName
    otherParticipant = room.participants.find(p => {
      const pUserId = String(p.userId || p.user_id || '');
      const hasValidName = p.userName && p.userName !== 'undefined' && typeof p.userName === 'string';
      return pUserId !== searchUserId && hasValidName;
    });
    
    // If still not found and current user is admin, try finding driver or officeAdmin
    if (!otherParticipant && currentUserRole === 'admin') {
      otherParticipant = room.participants.find(p => {
        const pUserId = String(p.userId || p.user_id || '');
        const hasValidName = p.userName && p.userName !== 'undefined' && typeof p.userName === 'string';
        return (p.userRole === 'driver' || p.userRole === 'officeAdmin') && 
               pUserId !== searchUserId && hasValidName;
      });
    }
    
    // Final fallback: any participant with valid name
    if (!otherParticipant) {
      otherParticipant = room.participants.find(p => {
        const pUserId = String(p.userId || p.user_id || '');
        const hasValidName = (p.userName && p.userName !== 'undefined') || 
                            (p.name && p.name !== 'undefined');
        return pUserId !== searchUserId && hasValidName;
      }) || (room.participants.length > 0 ? room.participants.find(p => 
        (p.userName && p.userName !== 'undefined') || (p.name && p.name !== 'undefined')
      ) : null);
    }
  }
  
  // Handle unreadCount - can be number or object
  let unreadCount = 0;
  if (typeof room.getUnreadCount === 'function') {
    unreadCount = room.getUnreadCount(currentUserId);
  } else if (room.unreadCount && typeof room.unreadCount === 'object') {
    unreadCount = room.unreadCount[currentUserId] || 0;
  } else if (typeof room.unreadCount === 'number') {
    // If it's a number, check if last message is not from current user
    const lastMessageSender = room.lastMessageSender || room.lastMessage?.senderId;
    if (lastMessageSender && lastMessageSender !== currentUserId) {
      unreadCount = room.unreadCount;
    }
  }

  const formatTime = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };

  const getAvatarColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return theme.colors.roleAdmin;
      case 'officeadmin':
        return theme.colors.roleOfficeAdmin;
      case 'driver':
        return theme.colors.roleDriver;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getInitials = (name) => {
    // Handle undefined, null, or invalid names
    if (!name || name === 'undefined' || name === 'null' || typeof name !== 'string') {
      return '?';
    }
    
    const trimmed = name.trim();
    if (!trimmed || trimmed.length === 0) return '?';
    
    // Filter out "undefined" strings
    const parts = trimmed.split(' ').filter(part => 
      part && part !== 'undefined' && part !== 'null' && part.trim().length > 0
    );
    
    if (parts.length >= 2) {
      const first = parts[0][0]?.toUpperCase() || '';
      const second = parts[1][0]?.toUpperCase() || '';
      if (first && second) {
        return first + second;
      }
    }
    
    // Fallback: use first character
    const firstChar = trimmed[0]?.toUpperCase();
    return firstChar || '?';
  };

  // Get avatar color based on role
  const getAvatarBgColor = (role) => {
    const colors = {
      'driver': { bg: '#fed7aa', border: '#fdba74', text: '#9a3412' }, // orange
      'officeadmin': { bg: '#e9d5ff', border: '#c084fc', text: '#6b21a8' }, // purple
      'admin': { bg: '#cffafe', border: '#67e8f9', text: '#164e63' }, // teal
    };
    return colors[role?.toLowerCase()] || { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
  };

  const avatarColors = getAvatarBgColor(otherParticipant?.userRole);
  const displayName = (otherParticipant?.userName && otherParticipant.userName !== 'undefined') ? otherParticipant.userName :
                     (otherParticipant?.name && otherParticipant.name !== 'undefined') ? otherParticipant.name :
                     (room.roomName && room.roomName !== 'undefined') ? room.roomName :
                     'Chat';
  const initials = getInitials(displayName);
  const hasUnread = unreadCount > 0;

  return (
    <div 
      className={`chat-list-item ${hasUnread ? 'active' : ''}`}
      onClick={onTap}
      style={{
        padding: '1rem',
        borderBottom: '1px solid ' + (hasUnread ? 'rgba(245, 158, 11, 0.1)' : '#f5f5f4'),
        backgroundColor: hasUnread ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
        borderLeft: hasUnread ? '3px solid #f59e0b' : 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem'
      }}
      onMouseEnter={(e) => {
        if (!hasUnread) {
          e.currentTarget.style.backgroundColor = '#fafaf9';
        }
      }}
      onMouseLeave={(e) => {
        if (!hasUnread) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      <div 
        style={{
          flexShrink: 0,
          position: 'relative',
          width: '3rem',
          height: '3rem'
        }}
      >
        <div
          style={{
          width: '3rem',
          height: '3rem',
          borderRadius: '9999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.875rem',
          border: `1px solid ${avatarColors.border}`,
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          backgroundColor: avatarColors.bg,
          color: avatarColors.text
        }}
      >
        {initials}
        </div>
        {/* Green online indicator - matches Flutter implementation */}
        {isOnline && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: '14px',
              height: '14px',
              backgroundColor: '#25D366', // WhatsApp green
              borderRadius: '50%',
              border: '2.5px solid white',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}
          />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '0.25rem'
        }}>
          <h4 style={{
            fontSize: hasUnread ? '0.875rem' : '0.875rem',
            fontWeight: hasUnread ? 700 : 600,
            color: hasUnread ? '#1c1917' : '#57534e',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            margin: 0
          }}>
            {displayName}
          </h4>
          {room.lastMessageTime && (
            <span style={{
              fontSize: '0.75rem',
              color: hasUnread ? '#78716c' : '#a8a29e',
              fontWeight: 500,
              flexShrink: 0,
              marginLeft: '0.5rem'
            }}>
              {formatTime(room.lastMessageTime)}
            </span>
          )}
        </div>
        <p style={{
          fontSize: '0.875rem',
          color: hasUnread ? '#1c1917' : '#78716c',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          margin: 0,
          fontWeight: hasUnread ? 500 : 400
        }}>
          {room.lastMessage || 'No messages yet'}
        </p>
      </div>
    </div>
  );
};

export default ChatRoomListItem;

